// ScanActionBar — three buttons that drive the scan flow.
//
//   [ Full scan ]    fresh 30-day Cartesian; wipes the pair_status ledger
//                    for this bootstrap signature. Confirms before firing
//                    because it's a 1-2 h run.
//   [ Daily scan ]   72-h incremental. Resume-aware (honors pair_status).
//                    Same backend as the 12 h auto-cron.
//   [ Continue ]     visible only when scan/term-status.has_incomplete.
//                    Same backend call as Daily; just a different label
//                    and confirm message ("Resume N pairs left").
//
// All three POST /api/scan/run {mode}. Server enforces a title-filter
// readiness gate (412 if not configured).

import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCw, Play, ChevronRight } from 'lucide-react';
import { apiGet, apiPost, ApiClientError } from '../../lib/api';
import type { ScanRunResponse, TermStatusResponse, TitleFilterState } from '../../lib/types';

export default function ScanActionBar() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<null | 'full' | 'daily' | 'continue'>(null);

  // Inline feedback banner — so a click ALWAYS produces a visible result,
  // even when the server skips the scan (e.g. daily_cooldown) and creates
  // no job. Follows the lightweight local-toast pattern used elsewhere
  // (ApplicationRow `syncedToast`); no global toast provider exists.
  const [feedback, setFeedback] =
    useState<{ kind: 'ok' | 'warn' | 'err' | 'info'; msg: string } | null>(null);
  const fbTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flash = (kind: 'ok' | 'warn' | 'err' | 'info', msg: string, ttl?: number) => {
    // ttl 0 = sticky (used for the transient "sending…" state, replaced on result)
    const dur = ttl ?? (kind === 'warn' ? 14000 : kind === 'err' ? 12000 : kind === 'ok' ? 9000 : 0);
    setFeedback({ kind, msg });
    if (fbTimer.current) clearTimeout(fbTimer.current);
    if (dur > 0) fbTimer.current = setTimeout(() => setFeedback(null), dur);
  };
  const relTime = (iso: string): string => {
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return 'soon';
    const ms = t - Date.now();
    if (ms <= 0) return 'shortly';
    const h = Math.floor(ms / 3600000);
    const m = Math.round((ms % 3600000) / 60000);
    return h > 0 ? `in ~${h}h${m ? ` ${m}m` : ''}` : `in ~${m}m`;
  };

  // Need has_incomplete + counts for the Continue button label.
  // Reuses the same query as ScanStatusBar (TanStack dedupes).
  const statusQ = useQuery({
    queryKey: ['scan-term-status'],
    queryFn: () => apiGet<TermStatusResponse>('/api/scan/term-status'),
    refetchInterval: (q) => (q.state.data?.in_flight ? 5_000 : 30_000),
    staleTime: 2_000,
  });

  // Title-filter readiness — same gate the server uses. Disable buttons
  // upstream so a click doesn't get a confusing 412.
  const titleFilterQ = useQuery({
    queryKey: ['title-filter'],
    queryFn: () => apiGet<TitleFilterState>('/api/title-filter'),
    staleTime: 30_000,
  });
  const titleFilterReady = !!titleFilterQ.data?.ready;
  const titleFilterMissing = titleFilterQ.data?.missing ?? [];

  const data = statusQ.data;
  const inFlight = !!data?.in_flight;
  const hasIncomplete = !!data?.has_incomplete;
  let incompleteCount = 0;
  if (data) {
    for (const t of data.terms) {
      incompleteCount += Math.max(0, t.summary.total - t.summary.completed - t.summary.backed_off);
    }
  }

  const runMut = useMutation({
    mutationFn: (mode: 'full' | 'daily') =>
      apiPost<ScanRunResponse>('/api/scan/run', { mode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scan-term-status'] });
      qc.invalidateQueries({ queryKey: ['auto-scan'] });
    },
  });

  // Turn the server's tick result into a visible banner. The server may
  // queue a job ({queued}) OR silently skip ({skipped}) — the skip case
  // (esp. daily_cooldown) used to produce ZERO feedback, which read as
  // "the button does nothing".
  const flashResult = (mode: 'full' | 'daily' | 'continue', res: ScanRunResponse | undefined) => {
    const run = res?.run ?? {};
    const label = mode === 'full' ? 'Full' : mode === 'continue' ? 'Resume' : 'Daily';
    if (run.queued) {
      flash('ok', `${label} scan started — running now. Live progress shows below.`);
      return;
    }
    if (run.skipped) { flash('warn', describeSkip(run)); return; }
    flash('ok', 'Scan request accepted.');
  };

  const describeSkip = (run: ScanRunResponse['run']): string => {
    switch (run.skipped) {
      case 'daily_cooldown':
        return `Already scanned recently — no new postings to pull yet` +
          `${run.nextAt ? ` (next auto-scan ${relTime(run.nextAt)})` : ''}. ` +
          `Click “Full scan” to force a fresh 30-day scrape now.`;
      case 'cooldown':
        return 'Scanned very recently. Try again later, or click “Full scan” to force one now.';
      case 'all_pairs_backing_off':
        return 'All sources are in a short cooldown after repeated failures — they’ll retry automatically.';
      case 'job_in_flight':
        return 'A scan is already running — see live progress below.';
      case 'no_prefs':
      case 'no_pairs':
        return 'No search terms configured yet. Set them in Settings before scanning.';
      case 'user_not_in_index':
        return 'Account isn’t ready yet — reload the page and try again.';
      default:
        return `Scan skipped (${run.skipped}).`;
    }
  };

  const titleFilterBlock = () => {
    flash('err', `Title filter incomplete — fill in ${titleFilterMissing.join(', ')} at Settings → Filter before scanning.`);
  };

  const onFull = async () => {
    if (busy) return;
    if (!titleFilterReady) { titleFilterBlock(); return; }
    const ok = confirm(
      'Run a FULL scan?\n\n' +
      'Wipes the per-term completion ledger and re-scrapes every (source × keyword) ' +
      'over the last 30 days. Can take 1-2 h on LinkedIn. You can close the browser; ' +
      'the scan keeps running on the server.'
    );
    if (!ok) return;
    setBusy('full');
    flash('info', 'Starting full scan…', 0);
    try { flashResult('full', await runMut.mutateAsync('full')); }
    catch (e) { flashErr(e); }
    finally { setBusy(null); }
  };

  const onDaily = async () => {
    if (busy) return;
    if (!titleFilterReady) { titleFilterBlock(); return; }
    const ok = confirm(
      'Run a DAILY scan?\n\n' +
      'Incremental pull (last 72 h), respecting the pair-status ledger so completed ' +
      'terms are skipped. Faster than a full scan.'
    );
    if (!ok) return;
    setBusy('daily');
    flash('info', 'Sending daily scan request…', 0);
    try { flashResult('daily', await runMut.mutateAsync('daily')); }
    catch (e) { flashErr(e); }
    finally { setBusy(null); }
  };

  const onContinue = async () => {
    if (busy) return;
    if (!titleFilterReady) { titleFilterBlock(); return; }
    const ok = confirm(
      `Continue the interrupted scan?\n\n` +
      `${incompleteCount} pair(s) still incomplete. Resumes only those — completed ` +
      `pairs are skipped. Same backend as Daily scan, just framed for the resume case.`
    );
    if (!ok) return;
    setBusy('continue');
    flash('info', 'Resuming scan…', 0);
    try { flashResult('continue', await runMut.mutateAsync('daily')); }
    catch (e) { flashErr(e); }
    finally { setBusy(null); }
  };

  const flashErr = (e: unknown) => {
    if (e instanceof ApiClientError && e.status === 412) {
      flash('err', 'Can’t start scan: title filter incomplete. Visit Settings → Filter.');
      return;
    }
    flash('err', `Scan request failed: ${(e as Error).message}`);
  };

  const fbStyle = (kind: 'ok' | 'warn' | 'err' | 'info'): React.CSSProperties => {
    switch (kind) {
      case 'ok':   return { color: '#1a7f37', background: '#eaf6ec', border: '1px solid #b6e0bf' };
      case 'warn': return { color: '#8a6d3b', background: '#fdf6e3', border: '1px solid #e3d08a' };
      case 'err':  return { color: '#b3261e', background: '#fdeceb', border: '1px solid #f0b8b3' };
      default:     return { color: 'var(--ink2)', background: 'var(--bg2, #f5f5f4)', border: '1px solid var(--line, #e5e5e5)' };
    }
  };

  const buttonStyle: React.CSSProperties = {
    fontSize: 12,
    padding: '6px 14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    whiteSpace: 'nowrap',
  };

  return (
    <div className="card" style={{
      padding: '10px 14px',
      marginBottom: 14,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      flexWrap: 'wrap',
    }}>
      <div className="tab" style={{ fontSize: 10.5, marginRight: 4 }}>Scan</div>
      <button
        className="btn primary"
        onClick={onFull}
        disabled={busy != null || inFlight || !titleFilterReady}
        title={!titleFilterReady
          ? `Blocked: title filter incomplete (${titleFilterMissing.join(', ')})`
          : 'Fresh 30-day scrape of every source × keyword. Wipes the per-term ledger.'}
        style={buttonStyle}
      >
        <Play size={11} />
        {busy === 'full' ? 'Starting…' : 'Full scan'}
      </button>
      <button
        className="btn"
        onClick={onDaily}
        disabled={busy != null || inFlight || !titleFilterReady}
        title={!titleFilterReady
          ? `Blocked: title filter incomplete (${titleFilterMissing.join(', ')})`
          : '72-h incremental. Skips terms already done in the current bootstrap.'}
        style={buttonStyle}
      >
        <RotateCw size={11} />
        {busy === 'daily' ? 'Starting…' : 'Daily scan'}
      </button>
      {hasIncomplete && !inFlight && (
        <button
          className="btn"
          onClick={onContinue}
          disabled={busy != null || !titleFilterReady}
          title={`Resume ${incompleteCount} incomplete pair${incompleteCount === 1 ? '' : 's'}.`}
          style={{
            ...buttonStyle,
            color: '#b88a2a',
            border: '1px solid #d6bb6b',
            background: '#fdf6e3',
          }}
        >
          <ChevronRight size={12} />
          {busy === 'continue'
            ? 'Resuming…'
            : `Continue (${incompleteCount} pair${incompleteCount === 1 ? '' : 's'} left)`}
        </button>
      )}
      {inFlight && (
        <span style={{ fontSize: 11, color: 'var(--ink3)', marginLeft: 'auto' }}>
          Scan in progress — stop it from the scan drawer below.
        </span>
      )}
      {feedback && (
        <div
          role="status"
          aria-live="polite"
          style={{
            flexBasis: '100%',
            marginTop: 4,
            fontSize: 12,
            lineHeight: 1.4,
            padding: '8px 12px',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            ...fbStyle(feedback.kind),
          }}
        >
          <span style={{ flex: 1 }}>{feedback.msg}</span>
          <button
            onClick={() => setFeedback(null)}
            aria-label="Dismiss"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'inherit', fontSize: 15, lineHeight: 1, padding: 0, opacity: 0.7,
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
